import ResetIcon from "@material-design-icons/svg/outlined/refresh.svg?react";
import {
  Button,
  cn,
  Dropdown,
  DropdownContent,
  DropdownItemPlain,
  DropdownPortal,
  DropdownTrigger,
  FilterSettingsButton,
  Slider,
  toast,
} from "@jana-technology/platform-ui";

import type { RiskAssetFiltersProps } from "./types";
import RiskAssetFilterItem from "./RiskAssetFilterItem";
import RiskAssetFilterItemGrid from "./RiskAssetFilterItemGrid";
import RiskAssetFilterItemSelect from "./RiskAssetFilterItemSelect";
import {
  generateRadioState,
  initSelectedRiskAssetFiltersState,
  keepStrings,
  useRiskManagementFiltersContext,
} from "contexts/filters/risk-management-filters";
import { useGlobalFiltersContext } from "contexts/filters/global-filters";
import type { FilteredWellNamesByHierarchy } from "contexts/filters/types";
import { parseWellIdNameParam, validateWellName } from "helpers/routing";
import { deriveRadioTreeBreadcrumbs } from "contexts/filters/helpers";
import { captureException } from "@sentry/react";

// consider extracting this to Platform UI post-launch
export const RiskAssetFilters = ({
  open,
  onOpenChange,
  activeFilterCount,
  onFilterButtonClick,
  onResetButtonClick,
  baseFilters,
  selectedRiskAssetFilters,
  setSelectedRiskAssetFilters,
  setSavedRiskAssetFilters,
  collisionBoundary,
}: RiskAssetFiltersProps) => {
  const {
    wellAgeRange,
    mostRecentCorrosionLogs,
    riskQuartiles,
    wellConfigurations,
    wellTypes,
  } = selectedRiskAssetFilters;

  const { flattenedHierarchyIds, hierarchiesWithWellNamesRadiosUnfiltered } =
    useGlobalFiltersContext();
  const {
    selectedWellName,
    fetchWellNamesByHierarchyRiskCharts,
    setRadioState,
    setFilteredWellNamesByHierarchyRiskCharts,
  } = useRiskManagementFiltersContext();

  const getNewWells = async () => {
    // refresh data, but don't sync w/ state and UI automatically
    const { data } = await fetchWellNamesByHierarchyRiskCharts({
      variables: {
        hierarchyIds: flattenedHierarchyIds,
        wellConfigurations: wellConfigurations.length
          ? wellConfigurations
          : undefined,
        mostRecentCorrosionLogs: mostRecentCorrosionLogs.length
          ? mostRecentCorrosionLogs
          : undefined,
        quartileId:
          riskQuartiles.length > 0 && riskQuartiles.length !== 4
            ? riskQuartiles[0].id
            : undefined,
        wellAgeFrom: wellAgeRange[0],
        wellAgeTo: wellAgeRange[1],
        wellType: wellTypes.length === 1 ? wellTypes[0] : undefined,
        isOverall: true,
      },
    });
    const filteredWellNames =
      data?.wellNamesGroupedByHierarchy?.filter(
        (item): item is FilteredWellNamesByHierarchy[number] =>
          typeof item.hierarchyId === "number",
      ) ?? [];

    return filteredWellNames;
  };

  const validateApplyRiskFilters = (
    filteredWellNames: FilteredWellNamesByHierarchy,
  ) => {
    // validate that there are wells
    // no badge count or selection list update because filters are not applied
    if (!filteredWellNames.length) {
      toast.warning(
        "No wells found for the current filters. Please select different filters.",
      );
      return;
    }
    // validate selected well name
    const [unresolvedHierarchyId, unresolvedWellName] = parseWellIdNameParam(
      selectedWellName?.value ?? "",
    );
    const [resolvedHierarchyId, resolvedWellName] = validateWellName(
      unresolvedHierarchyId,
      unresolvedWellName,
      filteredWellNames,
    );
    // if selected well doesn't exist any more, display warning and default well selection
    if (!resolvedWellName || resolvedHierarchyId === -1) {
      toast.warning(
        `The selected well name "${unresolvedWellName}" is not valid for the current filters. Please select a different well.`,
      );
      const resolvedHierarchyId = filteredWellNames[0].hierarchyId.toString();
      const resolvedWellName = filteredWellNames[0].wellNames.at(0) ?? "";
      // Update selected well to first available well
      const radioTreeBreadcrumbs = deriveRadioTreeBreadcrumbs(
        hierarchiesWithWellNamesRadiosUnfiltered,
        resolvedHierarchyId,
        `${resolvedHierarchyId}_${resolvedWellName}`,
      );
      setRadioState(generateRadioState(radioTreeBreadcrumbs));
    }
    // always update asset selection list and saved filters
    setFilteredWellNamesByHierarchyRiskCharts(filteredWellNames);
    setSavedRiskAssetFilters(selectedRiskAssetFilters);
  };

  return (
    <Dropdown open={open} onOpenChange={onOpenChange}>
      <DropdownTrigger>
        <FilterSettingsButton
          className={cn("rounded p-2", {
            "bg-grey-300": open,
            "fill-black hover:fill-grey-900": open,
          })}
          badgeCount={activeFilterCount}
          onClick={onFilterButtonClick}
        />
      </DropdownTrigger>
      <DropdownPortal>
        <DropdownContent
          collisionBoundary={collisionBoundary}
          collisionPadding={16}
          side="right"
          align="start"
          sideOffset={40}
          className="flex w-80 flex-col gap-4 p-4 shadow-toast [&>*]:outline-none [&>*]:focus:outline-none"
        >
          <DropdownItemPlain
            asChild
            onSelect={(event) => {
              event.preventDefault();
            }}
          >
            <header className="flex items-baseline justify-between">
              <h3 className="mr-2 font-semibold">Filters</h3>
              <button
                className="text-grey-700"
                onClick={(event) => {
                  setSelectedRiskAssetFilters(
                    initSelectedRiskAssetFiltersState(baseFilters),
                  );
                  onResetButtonClick?.(event);
                }}
              >
                <ResetIcon className="mr-1 inline h-4 w-4 rotate-180 -scale-y-100 fill-current" />
                Reset All
              </button>
            </header>
          </DropdownItemPlain>
          {baseFilters?.wellAgeRange && (
            <RiskAssetFilterItem heading="Well Age (years)">
              <Slider
                compact
                showInput
                value={wellAgeRange}
                onValueChange={(value) =>
                  setSelectedRiskAssetFilters((prev) => ({
                    ...prev,
                    wellAgeRange: value,
                  }))
                }
                min={baseFilters.wellAgeRange[0]}
                max={baseFilters.wellAgeRange[1]}
                step={1}
              />
            </RiskAssetFilterItem>
          )}
          {baseFilters?.wellConfigurations && (
            <RiskAssetFilterItem heading="Well Configuration">
              <RiskAssetFilterItemGrid
                stateKey="wellConfigurations"
                stateItems={wellConfigurations}
                setState={setSelectedRiskAssetFilters}
                baseFilters={baseFilters}
                items={baseFilters.wellConfigurations
                  .filter(keepStrings)
                  .map((config) => ({
                    children: `Config-${config}`,
                    value: config,
                  }))}
              />
            </RiskAssetFilterItem>
          )}
          {baseFilters?.mostRecentCorrosionLogs && (
            <RiskAssetFilterItem heading="Most Recent Intervention">
              <RiskAssetFilterItemGrid
                stateKey="mostRecentCorrosionLogs"
                stateItems={mostRecentCorrosionLogs}
                setState={setSelectedRiskAssetFilters}
                baseFilters={baseFilters}
                items={baseFilters.mostRecentCorrosionLogs
                  .filter(keepStrings)
                  .map((log) => ({
                    children: log,
                    value: log,
                  }))}
              />
            </RiskAssetFilterItem>
          )}
          {baseFilters.wellTypes && (
            <RiskAssetFilterItem heading="Well Type">
              <RiskAssetFilterItemSelect
                id="well-type"
                label="Well Type"
                stateKey="wellTypes"
                stateItems={wellTypes}
                setState={setSelectedRiskAssetFilters}
                baseFilters={baseFilters}
                items={baseFilters.wellTypes.filter(Boolean).map((type) => ({
                  children: type,
                  value: type,
                }))}
              />
            </RiskAssetFilterItem>
          )}
          {baseFilters.riskQuartiles && (
            <RiskAssetFilterItem heading="Risk (Quartile)">
              <RiskAssetFilterItemSelect
                id="risk-quartile"
                label="Risk (Quartile)"
                stateKey="riskQuartiles"
                stateItems={riskQuartiles}
                setState={setSelectedRiskAssetFilters}
                baseFilters={baseFilters}
                items={baseFilters.riskQuartiles.map((quartile) => ({
                  children: quartile.value,
                  value: String(quartile.id),
                }))}
              />
            </RiskAssetFilterItem>
          )}
          <DropdownItemPlain asChild>
            <Button
              onClick={() => {
                getNewWells()
                  .then((filteredWellNames) => {
                    // handle invalid cases before applying filters
                    validateApplyRiskFilters(filteredWellNames);
                  })
                  .catch((error: Error) => {
                    if (error.name === "AbortError") return;
                    toast.error(
                      "Failed to apply risk filters. Please try again.",
                    );
                    captureException(error, {
                      tags: {
                        component: "RiskAssetFilters",
                      },
                    });
                  });
              }}
            >
              Apply Filters
            </Button>
          </DropdownItemPlain>
        </DropdownContent>
      </DropdownPortal>
    </Dropdown>
  );
};

export default RiskAssetFilters;
